import React, {ChangeEventHandler, FocusEventHandler, forwardRef, InputHTMLAttributes} from 'react'

import cn from 'classnames/bind'

import FieldLabel from 'components/elements/fields/FieldLabel'
import {IFieldBase} from 'components/elements/fields/Input/Input'

import s from './InputQuantity.module.scss'

const cx = cn.bind(s)

export interface IInputQuantityProps
	extends Omit<InputHTMLAttributes<HTMLInputElement>, 'size' | 'step'>,
		IFieldBase {
	value?: number
	min?: number
	max?: number
	increment?: () => void
	decrement?: () => void
	variant?: 'default' | 'editable' | 'brainable' | 'new'
	size?: 'medium' | 'large'
	fullWidth?: boolean
	className?: string
	wrapperClassName?: string
	placeholder?: string
	onChange?: ChangeEventHandler<HTMLInputElement> | undefined
	onBlur?: FocusEventHandler<HTMLInputElement> | undefined
}

const InputQuantity = forwardRef<HTMLInputElement, IInputQuantityProps>(
	(
		{
			label,
			value = 1,
			min,
			max,
			increment,
			decrement,
			variant = 'default',
			size = 'medium',
			fullWidth,
			error,
			isError,
			wrapperClassName,
			className,
			onChange,
			onBlur,
			...props
		},
		forwardedRef,
	) => {
		return (
			<div
				className={cx('wrapper', 'global-input-wrapper', wrapperClassName, variant, size, {
					isError: isError || error,
				})}>
				{label && <FieldLabel variant={variant} label={label} required={props.required} />}

				<div className={cx('input-wrapper', fullWidth && 'full-width')}>
					<button
						className={cx('btn')}
						type="button"
						disabled={Number(value) === min}
						onClick={decrement}>
						<svg
							width="12"
							height="2"
							viewBox="0 0 12 2"
							fill="none"
							xmlns="http://www.w3.org/2000/svg">
							<path d="M0 0.999721C0 0.526334 0.383756 0.142578 0.857143 0.142578H11.1429C11.6162 0.142578 12 0.526334 12 0.999721C12 1.47311 11.6162 1.85686 11.1429 1.85686H0.857143C0.383756 1.85686 0 1.47311 0 0.999721Z" />
						</svg>
					</button>

					<input
						className={cx('input', className)}
						type="text"
						value={value}
						ref={forwardedRef}
						onChange={onChange}
						onBlur={onBlur}
						{...props}
					/>

					<button
						className={cx('btn')}
						type="button"
						disabled={!!max && Number(value) === max}
						onClick={increment}>
						<svg
							width="12"
							height="12"
							viewBox="0 0 12 12"
							fill="none"
							xmlns="http://www.w3.org/2000/svg">
							<path
								fillRule="evenodd"
								clipRule="evenodd"
								d="M6.85742 0.857143C6.85742 0.383756 6.47367 0 6.00028 0C5.52689 0 5.14314 0.383755 5.14314 0.857142V5.14258H0.857143C0.383756 5.14258 0 5.52633 0 5.99972C0 6.47311 0.383756 6.85686 0.857143 6.85686H5.14314V11.1429C5.14314 11.6162 5.52689 12 6.00028 12C6.47367 12 6.85742 11.6162 6.85742 11.1429V6.85686H11.1429C11.6162 6.85686 12 6.47311 12 5.99972C12 5.52633 11.6162 5.14258 11.1429 5.14258H6.85742V0.857143Z"
							/>
						</svg>
					</button>
				</div>
			</div>
		)
	},
)

InputQuantity.displayName = 'InputQuantity'

export default InputQuantity
